
'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Car, MessageSquare, Plus, Globe, Sparkles, User, LogOut, LayoutDashboard, Moon, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCountry } from '@/context/CountryContext';
import { useTheme } from '@/context/ThemeContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { cn } from '@/lib/utils';
import { TIPS_DATABASE } from '@/app/lib/tips-data';
import { useState, useEffect } from 'react';
import { useBranding } from '@/context/BrandingContext';
import { useLanguage } from '@/context/LanguageContext';
import {
  clearStoredSession,
  getDashboardPathForRole,
  getVendorWorkspacePath,
  notifySessionChanged,
  readStoredSession,
} from '@/lib/auth/session';

function useAuthStatus() {
  const [user, setUser] = useState<{ full_name: string; role: string; email: string } | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const syncAuth = () => {
      const session = readStoredSession();
      setUser(session.isAuthenticated ? (session.user as any) : null);
      setReady(true);
    };
    syncAuth();
    window.addEventListener('storage', syncAuth);
    window.addEventListener('s360-auth-changed', syncAuth);
    return () => {
      window.removeEventListener('storage', syncAuth);
      window.removeEventListener('s360-auth-changed', syncAuth);
    };
  }, []);

  const logout = async () => {
    const refresh = localStorage.getItem('s360_refresh');
    const API = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';
    if (refresh) {
      try {
        await fetch(`${API}/api/auth/logout/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh }),
        });
      } catch { }
    }
    clearStoredSession();
    notifySessionChanged();
    setUser(null);
    window.location.href = '/';
  };

  return { user, ready, logout };
}

export default function DesktopHeader() {
  const { setSelectedCountryCode, selectedCountry, selectedCountryCode, countries } = useCountry();
  const { lang, toggleLanguage, t } = useLanguage();
  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const pathname = usePathname();
  const { user, ready, logout } = useAuthStatus();
  const { isDark, toggleTheme } = useTheme();
  const { branding } = useBranding();

  useEffect(() => {
    if (TIPS_DATABASE.length === 0) return;
    const interval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % TIPS_DATABASE.length);
    }, 10000);
    return () => clearInterval(interval);
  }, []);

  const navLinks = [
    { label: t('nav_home'), href: '/' },
    { label: t('car_market'), href: '/buy' },
    { label: t('public_market'), href: '/marketplace' },
    { label: t('maintenance_short'), href: '/maintenance' },
    { label: t('parts'), href: '/parts' },
    { label: t('integrated_services'), href: '/services' },
    { label: t('smart_inspection'), href: '/diagnose' },
  ];

  const activeTip = TIPS_DATABASE[currentTipIndex] || { text_ar: 'مرحباً بك في سيارتي 360' };

  const initials = user?.full_name
    ?.split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('') || '؟';

  const dashboardHref =
    user?.role === 'vendor'
      ? getVendorWorkspacePath(user as any)
      : getDashboardPathForRole(user?.role);
  const profileHref =
    user?.role === 'vendor'
      ? '/myprofil'
      : getDashboardPathForRole(user?.role);

  return (
    <header className="hidden md:flex fixed top-0 left-0 right-0 z-50 bg-white/90 dark:bg-slate-900/95 backdrop-blur-md border-b border-slate-100 dark:border-slate-800 h-20 items-center justify-between px-8 mx-auto transition-colors duration-300">
      <div className="flex items-center gap-10">
        <Link href="/" className="flex items-center gap-2 shrink-0">
          {branding.logo_url ? (
            <img
              src={branding.logo_url}
              alt={branding.app_name_ar}
              className="h-10 w-auto object-contain"
            />
          ) : (
            <>
              <div className="bg-primary p-2 rounded-xl shadow-lg shadow-primary/20">
                <Car className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-black text-slate-800 dark:text-white tracking-tight">
                {branding.app_name_ar}<span className="text-primary">.com</span>
              </span>
            </>
          )}
        </Link>

        <nav className="flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className={cn(
                "text-sm font-bold transition-colors hover:text-primary",
                pathname === link.href || (pathname.startsWith('/buy') && link.href === '/buy') ? "text-primary" : "text-slate-500"
              )}
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>

      <div className="flex-1 max-w-md mx-8 hidden lg:flex items-center gap-3 px-5 py-2.5 bg-indigo-50/50 dark:bg-indigo-950/30 rounded-full border border-indigo-100 dark:border-indigo-900 overflow-hidden">
        <Sparkles className="h-4 w-4 text-primary shrink-0 animate-pulse" />
        <p className="text-[11px] font-bold text-slate-600 truncate animate-in slide-in-from-bottom-1 duration-500">
          {activeTip.text_ar}
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          className="rounded-full gap-2 border border-slate-100 bg-white hover:bg-slate-50 h-10 px-4 text-xs font-black"
          onClick={toggleLanguage}
        >
          <Globe className="h-4 w-4 text-primary" />
          {lang === 'ar' ? 'EN' : 'AR'}
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="rounded-full gap-2 border border-slate-100 bg-white hover:bg-slate-50 h-10 px-4">
              <Globe className="h-4 w-4 text-primary" />
              <span className="text-xs font-black">{selectedCountry?.name_ar || selectedCountry?.name}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-2xl w-48 p-2 shadow-xl border-none">
            <div className="p-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">اختر الدولة</div>
            {countries.map((c) => (
              <DropdownMenuItem
                key={c.code}
                onClick={() => setSelectedCountryCode(c.code)}
                className={cn(
                  "rounded-xl cursor-pointer font-bold text-sm py-2.5 flex items-center gap-2",
                  selectedCountryCode === c.code ? "bg-primary/10 text-primary" : ""
                )}
              >
                {c.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button asChild className="rounded-full px-6 bg-secondary hover:bg-secondary/90 shadow-lg shadow-secondary/20 h-10">
          <Link href="/sell" className="flex gap-2">
            <Plus className="h-4 w-4" />
            <span className="font-bold">أعلن الآن</span>
          </Link>
        </Button>

        <div className="flex items-center gap-1 border-r pr-3 mr-1 border-slate-200 dark:border-slate-700">
          {/* Dark mode toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-full h-10 w-10 text-slate-400 hover:text-primary hover:bg-blue-50 dark:hover:bg-blue-950"
            title={isDark ? 'تفعيل الوضع النهاري' : 'تفعيل الوضع الليلي'}
          >
            {isDark ? <Sun className="h-5 w-5 text-yellow-400" /> : <Moon className="h-5 w-5" />}
          </Button>

          <Link href="/chat">
            <Button variant="ghost" size="icon" className="rounded-full h-10 w-10 text-slate-400 hover:text-primary hover:bg-blue-50 dark:hover:bg-blue-950">
              <MessageSquare className="h-5 w-5" />
            </Button>
          </Link>

          {/* Auth section */}
          {ready && (
            <>
              {user ? (
                /* Logged-in user avatar dropdown */
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="h-10 w-10 rounded-full bg-blue-600 text-white font-black text-sm flex items-center justify-center border-2 border-blue-200 hover:border-blue-400 transition-colors shadow-sm cursor-pointer">
                      {initials}
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="rounded-2xl w-52 p-2 shadow-xl border-none">
                    <div className="px-3 py-2 mb-1">
                      <p className="font-black text-slate-800 text-sm truncate">{user.full_name}</p>
                      <p className="text-xs text-slate-400 truncate">{user.email?.includes('@public.sayarti360.local') ? user.role : user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild className="rounded-xl cursor-pointer font-bold text-sm py-2.5">
                      <Link href={dashboardHref} className="flex items-center gap-2">
                        <LayoutDashboard className="h-4 w-4 text-primary" />
                        لوحتي
                      </Link>
                    </DropdownMenuItem>
                    {user.role === 'vendor' && (
                      <DropdownMenuItem asChild className="rounded-xl cursor-pointer font-bold text-sm py-2.5">
                        <Link href={profileHref} className="flex items-center gap-2">
                          <User className="h-4 w-4 text-primary" />
                          ملفي الشخصي
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={logout}
                      className="rounded-xl cursor-pointer font-bold text-sm py-2.5 text-red-600 hover:bg-red-50 focus:bg-red-50 focus:text-red-600 flex items-center gap-2"
                    >
                      <LogOut className="h-4 w-4" />
                      تسجيل الخروج
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                /* Guest: show Sign In + Register */
                <div className="flex items-center gap-2">
                  <Button asChild variant="ghost" size="sm" className="rounded-full h-9 px-4 text-slate-600 hover:text-primary hover:bg-blue-50 font-bold text-sm">
                    <Link href="/login" className="flex items-center gap-1.5">
                      <User className="h-4 w-4" />
                      دخول
                    </Link>
                  </Button>
                  <Button asChild size="sm" className="rounded-full h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow-md shadow-blue-200">
                    <Link href="/public-register">إنشاء حساب</Link>
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </header>
  );
}
